import { clients } from 'lib/https';
import {
  DisableUserMfaRequest,
  DisableUserMfaResponse,
  GenerateOTPPayload,
  GenerateOTPResponse,
  IMFAConfiguration,
  ManageUserMFA,
  SetUpTotp,
  VerifyOTP,
  VerifyOTPResponse,
  VerifyTotpSetup,
} from '../types/mfa.types';
import { ConfigurationSettings } from '/models/configuration-settings';

const pendingGenerateOTPRequests = new Map<string, Promise<GenerateOTPResponse>>();
const pendingSetUpTotpRequests = new Map<string, Promise<unknown>>();

const getGenerateOTPRequestKey = (payload: GenerateOTPPayload) =>
  `${payload.userId}:${payload.mfaType}`;

const getSetUpTotpRequestKey = (payload: SetUpTotp) =>
  `${payload.userId}:${payload.projectKey}:${payload.OtpUploadtype}`;

const readStringField = (source: Record<string, unknown>, ...keys: string[]): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) {
      return value;
    }
  }
  return '';
};

export const unwrapGenerateOTPResponse = (response: unknown): GenerateOTPResponse => {
  const raw = response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
  const nested =
    raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
      ? (raw.data as Record<string, unknown>)
      : raw;

  const mfaId =
    readStringField(nested, 'mfaId', 'MfaId', 'itemId', 'ItemId') ||
    readStringField(raw, 'mfaId', 'MfaId', 'itemId', 'ItemId');
  const isSuccess = Boolean(
    nested.isSuccess ?? nested.IsSuccess ?? raw.isSuccess ?? raw.IsSuccess ?? mfaId
  );

  const errors = (nested.errors ?? raw.errors) as Record<string, string> | undefined;

  return {
    mfaId,
    isSuccess,
    ...(errors ? { errors } : {}),
  };
};

const readOptionalBoolean = (
  source: Record<string, unknown>,
  ...keys: string[]
): boolean | undefined => {
  for (const key of keys) {
    if (typeof source[key] === 'boolean') {
      return source[key] as boolean;
    }
  }
  return undefined;
};

export const unwrapVerifyOTPResponse = (response: unknown): VerifyOTPResponse => {
  const raw = response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
  const nested =
    raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
      ? (raw.data as Record<string, unknown>)
      : raw;

  const explicitIsValid =
    readOptionalBoolean(nested, 'isValid', 'valid') ??
    readOptionalBoolean(raw, 'isValid', 'valid');
  const explicitIsSuccess =
    readOptionalBoolean(nested, 'isSuccess', 'IsSuccess') ??
    readOptionalBoolean(raw, 'isSuccess', 'IsSuccess');
  const errors = (nested.errors ?? raw.errors) as Record<string, string> | undefined;
  const errorCode =
    readStringField(nested, 'error') || readStringField(raw, 'error');
  const errorDescription =
    readStringField(nested, 'error_description') ||
    readStringField(raw, 'error_description');
  const useId =
    readStringField(nested, 'useId', 'userId', 'UserId') ||
    readStringField(raw, 'useId', 'userId', 'UserId');
  const message = errors?.message || errorCode;
  const isFailure =
    Boolean(message) || explicitIsValid === false || explicitIsSuccess === false;
  const isValid = isFailure ? false : (explicitIsValid ?? true);
  const isSuccess = isFailure ? false : (explicitIsSuccess ?? true);

  return {
    isSuccess,
    isValid,
    useId,
    ...(errorCode ? { error: errorCode } : {}),
    ...(errorDescription ? { error_description: errorDescription } : {}),
    ...(message
      ? { errors: { ...errors, message, ...(errorDescription ? { error_description: errorDescription } : {}) } }
      : errors
        ? { errors }
        : {}),
  };
};

/**
 * Generates a One-Time Password (OTP) for the user.
 *
 * Concurrent calls for the same user and MFA type share a single in-flight request.
 * This prevents duplicate Generate POSTs when React Strict Mode remounts the setup modal.
 *
 * @param {string} params.userId - The user ID for whom the OTP is to be generated.
 *
 * @returns {Promise<GenerateOTPResponse>} A promise that resolves with the OTP generation response.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const otpResponse = await generateOTP({ userId: '12345' });
 * @param payload
 */
export const generateOTP = async (
  payload: GenerateOTPPayload,
  config: ConfigurationSettings
): Promise<GenerateOTPResponse> => {
  const requestKey = getGenerateOTPRequestKey(payload);
  const inFlightRequest = pendingGenerateOTPRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const requestPayload = {
    ...payload
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.generateOTP}`;
  const request = clients
    .post<unknown>(url, config.blocksKey, JSON.stringify(requestPayload))
    .then(unwrapGenerateOTPResponse)
    .finally(() => {
      pendingGenerateOTPRequests.delete(requestKey);
    });

  pendingGenerateOTPRequests.set(requestKey, request);
  return request;
};

/**
 * Verifies the OTP entered by the user.
 * Sends the OTP and necessary details to the server for validation.
 *
 * @param {VerifyOTP} payload - The OTP details including the verification code, mfaId, and authType.
 * @returns {Promise<VerifyOTPResponse>} The response indicating the success and validity of the OTP.
 * @throws {Error} Throws an error if the verification request fails.
 *
 * @example
 * const payload = { verificationCode: '123456', mfaId: 'mfa-id-123', authType: 1 };
 * const response = await verifyOTP(payload);
 * if (response.isSuccess && response.isValid) {
 *   console.log('OTP verified');
 * } else {
 *   console.log('Invalid OTP');
 * }
 */
export const verifyOTP = async (
  payload: VerifyOTP,
  config: ConfigurationSettings
): Promise<VerifyOTPResponse> => {
  const verifyOTPPayload: VerifyOTP = {
    mfaId: payload.mfaId,
    verificationCode: payload.verificationCode,
    authType: payload.authType,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.verifyOTP}`;
  const response = await clients.post<unknown>(
    url,
    config.blocksKey,
    JSON.stringify(verifyOTPPayload)
  );
  return unwrapVerifyOTPResponse(response);
};

/**
 * Verifies the TOTP code entered during authenticator app setup.
 *
 * @param {VerifyTotpSetup} payload - The TOTP code and user ID for setup verification.
 * @returns {Promise<VerifyOTPResponse>} The response indicating whether setup verification succeeded.
 */
export const verifyTotpSetup = async (
  payload: VerifyTotpSetup,
  config: ConfigurationSettings
): Promise<VerifyOTPResponse> => {
  const verifyTotpSetupPayload: VerifyTotpSetup = {
    code: payload.code,
    userId: payload.userId,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.verifyTotpSetup}`;
  const response = await clients.post<unknown>(
    url,
    config.blocksKey,
    JSON.stringify(verifyTotpSetupPayload)
  );
  return unwrapVerifyOTPResponse(response);
};

/**
 * Configures Multi-Factor Authentication (MFA) for the user.
 * Sends the MFA settings to the server to enable or disable MFA.
 *
 * @param {ManageUserMFA} payload - The MFA configuration details including the user ID and MFA settings.
 * @returns {Promise<ManageUserMFA>} The updated MFA configuration response.
 * @throws {Error} Throws an error if the configuration request fails.
 *
 * @example
 * const payload = { userId: 'user-id-123', mfaEnabled: true, userMfaType: 1 };
 * const response = await configureUserMfa(payload);
 * console.log(response); // Updated MFA settings
 */
export const configureUserMfa = async (
  payload: ManageUserMFA,
  config: ConfigurationSettings
): Promise<ManageUserMFA> => {
  const configureUserMfaPayload = {
    ...payload,
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.configureUserMfa}`;
  const res = await clients.post<{ data: ManageUserMFA }>(
    url,
    config.blocksKey,
    JSON.stringify(configureUserMfaPayload)
  );
  return res.data;
};

/**
 * Retrieves the TOTP (Time-based One-Time Password) setup details for the user.
 *
 * Concurrent calls for the same user and setup parameters share a single in-flight request.
 * This prevents duplicate setup POSTs when React Strict Mode remounts the setup modal.
 *
 * @param {Object} context - The context object containing query parameters for the request.
 * @param {SetUpTotp} context.queryKey - The query parameters to set up TOTP.
 * @returns {Promise<any>} The TOTP setup details in the response.
 * @throws {Error} Throws an error if the TOTP setup retrieval fails.
 * @example
 */
export const getSetUpTotp = async (
  context: { queryKey: [string, SetUpTotp] },
  config: ConfigurationSettings
): Promise<any> => {
  const payload = context.queryKey[1];
  const requestKey = getSetUpTotpRequestKey(payload);
  const inFlightRequest = pendingSetUpTotpRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.setUpTotp}`;
  const request = clients
    .post<any>(url, config.blocksKey, JSON.stringify({}))
    .finally(() => {
      pendingSetUpTotpRequests.delete(requestKey);
    });

  pendingSetUpTotpRequests.set(requestKey, request);
  return request;
};

/**
 * Sends a request to resend the OTP to the user based on the provided MFA ID.
 *
 * @param {string} mfaId - The MFA ID associated with the user for OTP resending.
 * @returns {Promise<any>} The response from the OTP resend request.
 * @throws {Error} Throws an error if the OTP resend fails.
 *
 * @example
 * const mfaId = 'user-mfa-id';
 * const response = await resendOtp(mfaId);
 * console.log(response); // Response after resending OTP
 */
export const resendOtp = async (mfaId: string, config: ConfigurationSettings): Promise<any> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.resendOTP}`;
  return await clients.post<any>(url, config.blocksKey, JSON.stringify({ mfaId }));
};

type MFAConfigurationApiResponse =
  | IMFAConfiguration
  | { data: IMFAConfiguration }
  | {
      enableMfa?: boolean;
      userMfaType?: number[];
    };

const unwrapMfaConfiguration = (response: MFAConfigurationApiResponse): Record<string, unknown> => {
  if (response && typeof response === 'object' && 'data' in response && response.data) {
    return response.data as unknown as Record<string, unknown>;
  }

  return response as unknown as Record<string, unknown>;
};

export const normalizeMfaConfiguration = (
  raw: Record<string, unknown> | IMFAConfiguration
): IMFAConfiguration => {
  const allowedMethods = Array.isArray(raw.allowedMethods)
    ? (raw.allowedMethods as number[])
    : Array.isArray((raw as { userMfaType?: number[] }).userMfaType)
      ? ((raw as { userMfaType?: number[] }).userMfaType ?? [])
      : [];

  return {
    enabled: Boolean(raw.enabled ?? (raw as { enableMfa?: boolean }).enableMfa),
    allowedMethods,
    requireMfaForAllUsers: Boolean(raw.requireMfaForAllUsers),
    mfaRequiredRoles: Array.isArray(raw.mfaRequiredRoles) ? (raw.mfaRequiredRoles as string[]) : [],
    mfaExemptRoles: Array.isArray(raw.mfaExemptRoles) ? (raw.mfaExemptRoles as string[]) : [],
    allowUserOptOut: raw.allowUserOptOut !== false,
    allowBackupCodes: raw.allowBackupCodes !== false,
    backupCodesCount: typeof raw.backupCodesCount === 'number' ? raw.backupCodesCount : 10,
  };
};

/**
 * Retrieves the project MFA configuration from IAM (`/iam/v4/MFA/Config`).
 * Project context is sent via the `x-blocks-key` header, not a query param.
 */
export const getMfaTemplate = async (config: ConfigurationSettings): Promise<IMFAConfiguration> => {
  const configurationPath = config.paths.mfa.getConfiguration?.trim();
  if (!configurationPath) {
    throw new Error('MFA configuration endpoint is not configured');
  }

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${configurationPath}`;
  const response = await clients.get<MFAConfigurationApiResponse>(url, config.blocksKey);

  return normalizeMfaConfiguration(unwrapMfaConfiguration(response));
};

/**
 * Disables Multi-Factor Authentication (MFA) for a specific user.
 * This service sends a POST request to disable MFA using the provided user ID.
 *
 * @param {string} userId - The ID of the user whose MFA is to be disabled.
 * @returns {Promise<DisableUserMfaResponse>} A promise that resolves with the disable MFA response.
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await disableUserMfa('user-123');
 * if (response.isSuccess) {
 *   console.log('MFA disabled successfully');
 * }
 */
export const disableUserMfa = async (
  userId: string,
  config: ConfigurationSettings
): Promise<DisableUserMfaResponse> => {
  const payload: DisableUserMfaRequest = {
    userId,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.disableUserMfa}`;
  return await clients.post<DisableUserMfaResponse>(url, config.blocksKey, JSON.stringify(payload));
};
